import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/SimulationConfig.css";

const CONFIG_URL = `${process.env.REACT_APP_BACKEND_ADDRESS}/rpt/config/allowrun`;

export default function SimulationConfig() {
  const [allowrun, setAllowrun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [passwordPromptOpen, setPasswordPromptOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(CONFIG_URL);
      if (response?.data?.description !== "ok" || typeof response?.data?.data?.allowrun !== "boolean") {
        throw new Error(response?.data?.description || "Invalid allowrun response");
      }
      setAllowrun(response.data.data.allowrun);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.description ||
          requestError?.message ||
          "Failed to load simulation config",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const requestAllowrunChange = () => {
    setPassword("");
    setPasswordError("");
    setPasswordPromptOpen(true);
  };

  const updateAllowrun = async (event) => {
    event.preventDefault();
    const nextValue = !allowrun;
    setSaving(true);
    setPasswordError("");
    try {
      const response = await axios.post(CONFIG_URL, { allowrun: nextValue, password });
      if (response?.data?.description !== "ok" || typeof response?.data?.data?.allowrun !== "boolean") {
        throw new Error(response?.data?.description || "Failed to update simulation config");
      }
      setAllowrun(response.data.data.allowrun);
      setPasswordPromptOpen(false);
      setPassword("");
    } catch (requestError) {
      setPasswordError(
        requestError?.response?.data?.description ||
          requestError?.message ||
          "Failed to update simulation config",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="simulation-config">
      <div className="simulation-config__heading">
        <h1>Simulation Config</h1>
        <button
          type="button"
          className="simulation-config__refresh"
          onClick={loadConfig}
          disabled={loading || saving}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <p className="simulation-config__error" role="alert">{error}</p>}

      <section className="simulation-config__control" aria-busy={loading || saving}>
        <div>
          <h2>Allow simulation runs</h2>
          <p
            className={`simulation-config__status ${allowrun === null ? "" : allowrun ? "is-enabled" : "is-disabled"}`}
            role="status"
            aria-live="polite"
          >
            {loading && allowrun === null ? "Loading" : allowrun === null ? "Unavailable" : String(allowrun)}
          </p>
        </div>
        <button
          type="button"
          className={`simulation-config__toggle ${allowrun ? "is-stop" : "is-start"}`}
          onClick={requestAllowrunChange}
          disabled={allowrun === null || loading || saving}
        >
          {allowrun ? "Stop simulations" : "Allow simulations"}
        </button>
      </section>

      {passwordPromptOpen && (
        <div className="simulation-config__modal-backdrop">
          <form
            className="simulation-config__password-dialog"
            onSubmit={updateAllowrun}
            role="dialog"
            aria-modal="true"
            aria-labelledby="simulation-config-password-title"
          >
            <h2 id="simulation-config-password-title">Confirm configuration change</h2>
            <label htmlFor="simulation-config-password">Password</label>
            <input
              id="simulation-config-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              disabled={saving}
            />
            {passwordError && <p className="simulation-config__error" role="alert">{passwordError}</p>}
            <div className="simulation-config__dialog-actions">
              <button
                type="button"
                className="simulation-config__refresh"
                onClick={() => setPasswordPromptOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`simulation-config__toggle ${allowrun ? "is-stop" : "is-start"}`}
                disabled={saving || !password}
              >
                {saving ? "Updating..." : "Confirm"}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}